import { ChangeEvent, RefObject, useCallback, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { debounce } from "es-toolkit/function"
import { ChatTextarea } from "@/components/chat-textarea"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useAppendChatHistoryMessage, useChatHistoryMessages } from "@/modules/chats/hooks/useChatHistory"
import { useChatInputContext, useChatInputStateGetter } from "@/modules/chats/stores/useChatInputStore"
import { useUnsentMessageContext } from "@/modules/chats/stores/useUnsentMessageStore"
import { UnsentChatMessage } from "@/modules/chats/types/chat-message"
import { ChatThread } from "@/modules/chats/types/chat-thread"

type ChatComposerTextareaProps = {
  selectedThread: ChatThread
  textareaRef: RefObject<HTMLTextAreaElement | null>
}

export const ChatComposerTextarea = (props: ChatComposerTextareaProps) => {
  const { selectedThread, textareaRef } = props

  const { t } = useTranslation()

  const aiRunning = useChatInputContext((store) => store.aiRunning)
  const isSendingMessage = useChatInputContext((store) => store.isSendingMessage)
  const aiTrackingMetadata = useChatInputContext((store) => store.aiTrackingMetadata)
  const messageText = useChatInputContext((store) => store.messageText)
  const setMessageText = useChatInputContext((store) => store.setMessageText)
  const getChatInputState = useChatInputStateGetter()

  const setUnsentMessage = useUnsentMessageContext((state) => state.setUnsentMessage)

  const historyMessages = useChatHistoryMessages()
  const appendHistory = useAppendChatHistoryMessage()

  const prevHistoryRef = useRef("")

  const isAiRunning = aiRunning !== "NONE"
  const isAiGeneratingComposerText = !["NONE", "NEXT_BEST_QUESTIONS"].includes(aiRunning)

  const chatEventTracking = useCallback(
    (name: string) => {
      FaroInsightsWrapper.pushEvent(name, {
        chatThreadId: selectedThread.id,
        patientId: selectedThread.patient.id,
        appointmentId: selectedThread.appointmentId,
      })
    },
    [selectedThread],
  )

  const handleChangeDebounce = useMemo(
    () =>
      debounce((newValue: string, inputType: string) => {
        // This line for preventing re-show old text when using hotkey to send message
        if (getChatInputState().messageText !== newValue) return

        if (inputType === "insertFromPaste" || newValue.trimEnd() !== prevHistoryRef.current.trimEnd()) {
          appendHistory(newValue)
          prevHistoryRef.current = newValue
        }
      }, 500),
    [getChatInputState, appendHistory],
  )

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setMessageText(newValue)

    const inputEvent = e.nativeEvent as InputEvent
    handleChangeDebounce(newValue, inputEvent.inputType)
  }

  useEffect(() => {
    const currentMessage = historyMessages.at(-1)
    if (currentMessage) {
      const { content, contentBeforeAiFeature, aiFeatures } = currentMessage

      const message: UnsentChatMessage = {
        sendContent: content,
        contentBeforeAiFeature: contentBeforeAiFeature ?? null,
        aiFeatures,
        aiTrackingMetadata: aiTrackingMetadata,
      }

      setUnsentMessage(selectedThread.id, message)
      setMessageText(content)
      prevHistoryRef.current = content
    }
  }, [aiTrackingMetadata, historyMessages, selectedThread.id, setMessageText, setUnsentMessage])

  return (
    <ChatTextarea
      ref={textareaRef}
      data-testid="chat-field"
      className="group-hover:bg-surface-subdued-hovered max-h-64 min-h-22! resize-none scroll-pb-13.5 overflow-y-auto pb-13.5 disabled:cursor-not-allowed"
      placeholder={t(`chat.input.workingOn.${aiRunning}`)}
      value={isAiGeneratingComposerText ? "" : messageText}
      onChange={handleChange}
      disabled={isAiRunning || isSendingMessage}
      onBlur={() => chatEventTracking("chat_message_blur")}
      onFocus={() => chatEventTracking("chat_message_focus")}
    />
  )
}
